// src/pages/EditResourcePage.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, GeoPoint, collection, getDocs } from 'firebase/firestore';
import { useJsApiLoader } from '@react-google-maps/api';
import './EditResourcePage.css';

const Toast = ({ message, show }) => {
  if (!show) return null;
  return <div className="toast-notification">{message}</div>;
};

// --- UPDATED: Restored the full functionality to the alert modal ---
const CustomAlertModal = ({ message, onConfirm }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleConfirm = () => {
    if (dontShowAgain) {
      localStorage.setItem('hasDismissedGpsAlert', 'true');
    }
    onConfirm();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>GPS Coordinates Found!</h2>
        <p>{message}</p>
        <div className="modal-checkbox">
          <input
            type="checkbox"
            id="dontShowAgain"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
          />
          <label htmlFor="dontShowAgain">Don't show this message again</label>
        </div>
        <div className="modal-actions">
          <button className="btn-confirm-delete" onClick={handleConfirm}>OK</button>
        </div>
      </div>
    </div>
  );
};

// --- (No changes to this component) ---
const CheckboxGroup = ({ title, options, selectedOptions, handleSelect }) => {
  return (
    <div className="checkbox-group-container">
      <p className="checkbox-group-title">{title}</p>
      <div className="checkbox-grid">
        {options.map((option) => (
          <label key={option.id} className="checkbox-label">
            <input
              type="checkbox"
              value={option.id}
              checked={selectedOptions.includes(option.id)}
              onChange={() => handleSelect(option.id)}
            />
            {option.name}
          </label>
        ))}
      </div>
    </div>
  );
};

function EditResourcePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['geocoding'],
  });
  
  const [formData, setFormData] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const resourceDocRef = doc(db, 'resources', id);
        const resourcePromise = getDoc(resourceDocRef);

        const categoriesCollectionRef = collection(db, 'categories');
        const categoriesPromise = getDocs(categoriesCollectionRef);

        const [resourceDocSnap, categoriesSnapshot] = await Promise.all([resourcePromise, categoriesPromise]);

        const categoryList = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllCategories(categoryList);

        if (resourceDocSnap.exists()) {
          const data = resourceDocSnap.data();
          if (data.coordinates) {
            data.latitude = data.coordinates.latitude;
            data.longitude = data.coordinates.longitude;
          }
          setFormData(data);
        } else {
          console.error("No such document!");
          alert("Resource not found.");
          navigate('/dashboard');
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        alert("Failed to load data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (categoryId) => {
    setFormData(prev => {
      const currentCategoryIds = prev.categoryIDs || [];
      const newCategoryIds = currentCategoryIds.includes(categoryId)
        ? currentCategoryIds.filter(id => id !== categoryId)
        : [...currentCategoryIds, categoryId];
      return { ...prev, categoryIDs: newCategoryIds };
    });
  };

  // --- UPDATED: Added the check for localStorage before showing the alert ---
  const handleCalculateGps = () => {
    if (!formData.location || formData.location.trim() === '') {
      alert('Please enter a location address before calculating GPS.');
      return;
    }
    setIsCalculating(true);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: formData.location }, (results, status) => {
      setIsCalculating(false);
      if (status === 'OK') {
        const location = results[0].geometry.location;
        setFormData(prev => ({
          ...prev,
          latitude: location.lat(),
          longitude: location.lng(),
        }));
        
        const hasDismissed = localStorage.getItem('hasDismissedGpsAlert') === 'true';
        if (!hasDismissed) {
          setAlertMessage(`The fields have been filled for you.\nLatitude: ${location.lat()}\nLongitude: ${location.lng()}\n\nPlease remember to click "Save Changes" to apply.`);
          setShowAlert(true);
        }
      } else {
        alert('Geocode was not successful for the following reason: ' + status);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!formData.categoryIDs || formData.categoryIDs.length === 0) {
      alert("Please select at least one 'Type of Resource'.");
      return;
    }
    setIsSubmitting(true);
    try {
      const { latitude, longitude, ...restOfData } = formData;
      const dataToUpdate = { ...restOfData };

      if (latitude && longitude) {
        dataToUpdate.coordinates = new GeoPoint(parseFloat(latitude), parseFloat(longitude));
      } else {
        delete dataToUpdate.coordinates;
      }
      
      delete dataToUpdate.resourceType;
      delete dataToUpdate.flagMessage;

      const resourceDocRef = doc(db, 'resources', id);
      await updateDoc(resourceDocRef, dataToUpdate);
      
      setShowToast(true);
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (error) {
      console.error("Error updating document: ", error);
      alert(`Error updating resource: ${error.message}`);
      setIsSubmitting(false);
    }
  };

  if (loadError) return <div>Error loading maps. Please check your API key.</div>;
  if (loading || !formData) return <div className="page-container"><p>Loading resource...</p></div>;

  return (
    <div className="page-container">
      <Toast message="Changes Saved!" show={showToast} />
      {showAlert && <CustomAlertModal message={alertMessage} onConfirm={() => setShowAlert(false)} />}
      <div className="form-container">
        <h1 className="form-header">Edit Resource</h1>
        <form onSubmit={handleSubmit}>
          
          <div className="form-group">
            <label htmlFor="organizationName" className="form-label">Name of your Organization</label>
            <input type="text" id="organizationName" name="organizationName" value={formData.organizationName || ''} onChange={handleChange} className="form-input" required />
          </div>

          <div className="form-group">
            <label htmlFor="representativeName" className="form-label">Name of the Representative</label>
            <input type="text" id="representativeName" name="representativeName" value={formData.representativeName || ''} onChange={handleChange} className="form-input" required />
          </div>

          <div className="form-group">
            <CheckboxGroup
              title="Type of Resource (select all that apply)"
              options={allCategories}
              selectedOptions={formData.categoryIDs || []}
              handleSelect={handleCategoryChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="location" className="form-label">Location Address</label>
            <input type="text" id="location" name="location" value={formData.location || ''} onChange={handleChange} className="form-input" required />
          </div>
          
          {!formData.latitude && !formData.longitude && (
            <div className="form-group-action">
              <button type="button" onClick={handleCalculateGps} className="calculate-gps-button" disabled={isCalculating || !isLoaded}>
                {isCalculating ? 'Calculating...' : 'Calculate GPS from Address'}
              </button>
            </div>
          )}

          <p className="form-label-optional">Or, enter GPS coordinates directly below.</p>
          <div className="coordinate-group">
            <div className="form-group">
                <label htmlFor="latitude" className="form-label">Latitude</label>
                <input type="number" step="any" id="latitude" name="latitude" value={formData.latitude || ''} onChange={handleChange} className="form-input" placeholder="e.g., 40.7128" />
            </div>
            <div className="form-group">
                <label htmlFor="longitude" className="form-label">Longitude</label>
                <input type="number" step="any" id="longitude" name="longitude" value={formData.longitude || ''} onChange={handleChange} className="form-input" placeholder="e.g., -74.0060" />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="couponCode" className="form-label">Coupon Code (Optional)</label>
            <input type="text" id="couponCode" name="couponCode" value={formData.couponCode || ''} onChange={handleChange} className="form-input" />
          </div>

          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default EditResourcePage;